import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { nodeApi } from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import RegisterForm from './RegisterForm';
import ForgotPasswordForm from './ForgotPasswordForm';

const LoginPage = ({ onClose: _onClose, embedded }) => {
    const { t } = useTranslation();
    const { login } = useAuth();
    const [formData, setFormData] = useState({
        username: '',
        password: '',
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [currentView, setCurrentView] = useState('login');

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const result = await login(formData.username, formData.password);

            if (!result.success) {
                setError(result.error);
            }
        } catch (err) {
            setError(t('login.unexpected_error'));
        } finally {
            setLoading(false);
        }
    };

    const handleLinkedInLogin = () => {
        window.location.href = `${nodeApi.defaults.baseURL}/api/auth/linkedin`;
    };

    const handleFranceConnectLogin = () => {
        alert(t('login.franceconnect_soon'));
    };

    if (currentView === 'register') {
        return <RegisterForm onBackToLogin={() => setCurrentView('login')} embedded={embedded} />;
    }

    if (currentView === 'forgot-password') {
        return <ForgotPasswordForm onBackToLogin={() => setCurrentView('login')} embedded={embedded} />;
    }

    const outerShell = embedded
        ? 'bg-white rounded-xl shadow-xl p-6 sm:p-8'
        : 'min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8';

    return (
        <div className={outerShell}>
            <div className="max-w-md w-full space-y-8">
                <div>
                    {!embedded && (
                        <>
                            <div className="flex justify-center">
                                <svg className="w-12 h-12 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                </svg>
                            </div>
                            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                                {t('login.brand_title')}
                            </h2>
                        </>
                    )}
                    <p className={embedded ? 'mt-0 text-center text-sm text-gray-600' : 'mt-2 text-center text-sm text-gray-600'}>
                        {t('login.subtitle')}
                    </p>
                </div>

                <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
                            <span className="block sm:inline">{error}</span>
                        </div>
                    )}

                    <div className="rounded-md shadow-sm -space-y-px">
                        <div>
                            <label htmlFor="username" className="sr-only">
                                {t('login.username_placeholder')}
                            </label>
                            <input
                                id="username"
                                name="username"
                                type="text"
                                required
                                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                                placeholder={t('login.username_placeholder')}
                                value={formData.username}
                                onChange={handleChange}
                                disabled={loading}
                            />
                        </div>
                        <div>
                            <label htmlFor="password" className="sr-only">
                                {t('login.password_placeholder')}
                            </label>
                            <input
                                id="password"
                                name="password"
                                type="password"
                                required
                                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                                placeholder={t('login.password_placeholder')}
                                value={formData.password}
                                onChange={handleChange}
                                disabled={loading}
                            />
                        </div>
                    </div>

                    <div className="flex items-center justify-between">
                        <button
                            type="button"
                            className="text-sm text-blue-600 hover:text-blue-500"
                            onClick={() => setCurrentView('forgot-password')}
                        >
                            {t('login.forgot')}
                        </button>
                    </div>

                    <div>
                        <button
                            type="submit"
                            disabled={loading}
                            className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? <LoadingSpinner size="small" color="white" /> : t('login.submit')}
                        </button>
                    </div>

                    <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-300" />
                        </div>
                        <div className="relative flex justify-center text-sm">
                            <span className={`px-2 text-gray-500 ${embedded ? 'bg-white' : 'bg-gray-50'}`}>
                                {t('login.divider')}
                            </span>
                        </div>
                    </div>

                    <div className="space-y-3">
                        <button
                            type="button"
                            onClick={handleLinkedInLogin}
                            className="w-full flex justify-center items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="#0077B5">
                                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                            </svg>
                            {t('login.linkedin')}
                        </button>

                        <button
                            type="button"
                            onClick={handleFranceConnectLogin}
                            className="w-full flex justify-center items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            <div className="w-5 h-5 mr-2 bg-blue-600 rounded flex items-center justify-center">
                                <span className="text-white text-xs font-bold">FC</span>
                            </div>
                            {t('login.franceconnect')}
                        </button>
                    </div>

                    <div className="text-center">
                        <button
                            type="button"
                            className="text-sm text-blue-600 hover:text-blue-500"
                            onClick={() => setCurrentView('register')}
                        >
                            {t('login.register_prompt')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default LoginPage;
